import { API_CONFIG } from '../config/api.ts';
import { authenticatedApiCall } from './api.ts';

// Types for Balance API
export interface CurrentDateResponse {
  date: string;
  timezone: string;
}

export interface BalanceEntry {
  id?: number;
  employeeId: number;
  type: 'server' | 'host' | 'bar' | 'manager';
  cashDue: number;
  shift: 'AM' | 'PM';
  netSales: number;
}

export interface PaidOutEntry {
  id?: number;
  description: string;
  cashDue: number;
}

export interface BusserTipEntry {
  id?: number;
  busserName: string;
  tips: number;
}

export interface EventServerBartenderTipEntry {
  id?: number;
  eventServerBartender: string;
  pooledTips: number;
}

export interface NegativeCashOutEntry {
  id?: number;
  tookFrom: string;
  negativeCashOuts: number;
}

export interface KenoCashEntry {
  id?: number;
  employeeId: number;
  kenoCash: number;
}

export interface RegisterEntry {
  id?: number;
  registerType: string;
  employeeId: number | null;
  closeOutAmount: number;
}

export interface BarTipEntry {
  id?: number;
  bartenderName: string;
  tipAmount: number;
}

export interface ExpoTipEntry {
  id?: number;
  expoName: string;
  tipAmount: number;
}

export interface BalanceData {
  balance: BalanceEntry[];
  paid_outs: PaidOutEntry[];
  busser_tips: BusserTipEntry[];
  event_servers_bartender_tips: EventServerBartenderTipEntry[];
  negative_cash_outs: NegativeCashOutEntry[];
  keno_cash: KenoCashEntry[];
  register: RegisterEntry[];
  bar_tips: BarTipEntry[];
  expo_tips: ExpoTipEntry[];
}

export interface BulkBalanceRequest {
  date?: string;
  balance: Array<{
    employeeId: number;
    type: 'server' | 'host' | 'bar' | 'manager';
    cashDue: number;
    shift: 'AM' | 'PM';
    netSales: number;
  }>;
  paid_outs: Array<{
    description: string;
    cashDue: number;
  }>;
  busser_tips: Array<{
    busserName: string;
    tips: number;
  }>;
  event_servers_bartender_tips: Array<{
    eventServerBartender: string;
    pooledTips: number;
  }>;
  negative_cash_outs: Array<{
    tookFrom: string;
    negativeCashOuts: number;
  }>;
  keno_cash: Array<{
    employeeId: number;
    kenoCash: number;
  }>;
  register: Array<{
    registerType: string;
    employeeId: number | null;
    closeOutAmount: number;
  }>;
  bar_tips: Array<{
    bartenderName: string;
    tipAmount: number;
  }>;
  expo_tips: Array<{
    expoName: string;
    tipAmount: number;
  }>;
}

// Balance API calls
export const balanceApi = {
  // Get current Massachusetts date (using tipout endpoint)
  getCurrentDate: async (token: string): Promise<CurrentDateResponse> => {
    return authenticatedApiCall<CurrentDateResponse>(
      API_CONFIG.ENDPOINTS.BALANCE.CURRENT_DATE,
      {
        method: 'GET',
      },
      token
    );
  },

  // Get all distinct dates from balance table
  getDistinctDates: async (token: string): Promise<string[]> => {
    return authenticatedApiCall<string[]>(
      API_CONFIG.ENDPOINTS.BALANCE.BASE,
      {
        method: 'GET',
      },
      token
    );
  },

  // Get balance data for a specific date
  getBalanceByDate: async (token: string, date: string): Promise<BalanceData> => {
    return authenticatedApiCall<BalanceData>(
      `${API_CONFIG.ENDPOINTS.BALANCE.BASE}/${date}`,
      {
        method: 'GET',
      },
      token
    );
  },

  // Save balance data (bulk replace)
  saveBalance: async (token: string, data: BulkBalanceRequest): Promise<BalanceData> => {
    return authenticatedApiCall<BalanceData>(
      API_CONFIG.ENDPOINTS.BALANCE.BASE,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token
    );
  },
};

